import { Box, Button, Card, Checkbox, FormControlLabel, Grid, TextField, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Alert, Tabs, Tab } from "@mui/material";
import axios from "../api/axios";
import { useContext, useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import AuthContext from "../contexts/AuthProvider";
import { brandColors } from '../theme/colors';
import { useTranslation } from 'react-i18next';

const  LoginPage= () => {
        const { t } = useTranslation('global');
        // Rileva il tema corrente dal localStorage
        const [currentTheme, setCurrentTheme] = useState(() => {
                return localStorage.getItem('themeMode') || 'soultech';
        });
        
        // Usa i colori del tema corrente
        const themeColors = useMemo(() => {
                return brandColors[currentTheme] || brandColors.soultech;
        }, [currentTheme]);
        
        // Determina se il tema è scuro (per stili form)
        const isDarkTheme = currentTheme === 'dark' || currentTheme === 'brown';

        const textFieldStyle = {
                '& .MuiInputLabel-root': { 
                        color: themeColors.text.secondary, 
                        fontWeight: 600,
                        transform: 'translate(14px, -9px) scale(0.75)',
                        backgroundColor: isDarkTheme ? themeColors.login.inputBg : 'white',
                        padding: '0 4px'
                },
                '& .MuiInputLabel-root.Mui-focused': { color: themeColors.primary, fontWeight: 700 },
                '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: themeColors.border.light, borderWidth: 2 },
                        '&:hover fieldset': { borderColor: themeColors.primary },
                        '&.Mui-focused fieldset': { borderColor: themeColors.primary, borderWidth: 2 },
                        '& input': { color: themeColors.text.primary, fontWeight: 500 }
                }
        };

        const {setAuth, persist, setPersist} =useContext(AuthContext);
        const [email, setEmail] = useState("");
        const [password, setPassword] = useState("");
        const [loginError, setLoginError] = useState(false);
        const [errorMessage, setErrorMessage] = useState("");
        const [loginType, setLoginType] = useState(0); // 0 = Cliente, 1 = Super Admin

        const navigate = useNavigate();
        
        


        
        const togglePersist=()=>{
                setPersist(prev=> !prev);
        }

        useEffect(()=>{
                localStorage.setItem("persist",persist);
        },[persist])
        
        const login = () =>{
                setLoginError(false); // Reset error state
                
                // Usa sempre lo stesso endpoint /auth
                const endpoint = "/auth";
                
                // Prepara i dati del login
                const loginData = loginType === 1 
                        ? { email: email, password: password, loginType: "superadmin" }
                        : { email: email, password: password, loginType: "customer" };
                
                axios.post(endpoint,
                        JSON.stringify(loginData),
                        {
                                headers:{'Content-Type': 'application/json'},
                                withCredentials: true
                        }
                ).then((response) => {

                        if (loginType === 1) {
                                // Login Super Admin
                                if (response.data.accessToken) {
                                        const accessToken = response.data.accessToken;
                                        const userType = response.data.userType || 'superadmin';
                                        
                                        // Salva il token nel localStorage per le API SaaS
                                        localStorage.setItem('adminToken', accessToken);
                                        
                                        const authData = {
                                                email: email,
                                                password,
                                                accessToken,
                                                userType: 'superadmin'
                                        };
                                        
                                        setAuth(authData);
                                        
                                        setEmail("");
                                        setPassword("");
                                        
                                        // Reindirizza alla dashboard SaaS Admin
                                        const adminPath = process.env.REACT_APP_ADMIN_PATH || '/admin';
                                        navigate(adminPath + '/dashboard', { replace: true });
                                } else {
                                        setLoginError(true);
                                        setErrorMessage(response.data.message || "Credenziali non valide");
                                }
                        } else {
                                // Login Cliente - gestisce anche la risposta multi-tenant
                                        if (response.data.accessToken) {
                                        const accessToken = response.data.accessToken;
                                        const userType = response.data.userType || 'customer';
                                        const customerCode = response.data.customerCode;
                                                const customerDatabase = response.data.customerDatabase;
                                        const customer_id = response.data.customer_id;
                                        const fullName = response.data.fullName;
                                        const role = response.data.role;
                                        
                                        const authData = {
                                                email,
                                                password,
                                                accessToken, 
                                                userType,
                                                customerCode,
                                                        customerDatabase,
                                                customer_id,
                                                fullName,
                                                role
                                        };
                                        
                                        setAuth(authData);
                                        
                                        setEmail("");
                                        setPassword("");
                                        
                                        const basePath = process.env.REACT_APP_BASE_PATH || '/dashboard';
                                        navigate(basePath, { replace: true });
                                } else{
                                        setLoginError(true);
                                        setErrorMessage(response.data.message || "Credenziali non valide");
                                }
                        }
                }).catch(function(error){
                        setLoginError(true);
                        
                        if(error.response?.status === 401) {
                                setErrorMessage(t('login.errors.invalidCredentials', 'Email o password non corretti'));
                        } else if(error.response?.status === 500) {
                                setErrorMessage(t('login.errors.serverError', 'Errore del server. Riprova più tardi'));
                        } else if(error.code === 'ERR_NETWORK') {
                                setErrorMessage(t('login.errors.networkError', 'Impossibile connettersi al server'));
                        } else {
                                setErrorMessage(t('login.errors.genericError', 'Errore durante il login. Riprova'));
                        }
                })
        };
        

        return (
                <Box 
                        component="main" 
                        sx={{ 
                                minHeight: '100vh',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: themeColors.gradient.darkBg,
                                py: 4,
                                px: 2
                        }}
                >
                        <Grid container sx={{ maxWidth: 1200, minHeight: { xs: 'auto', md: '600px' }, boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', borderRadius: '12px' }}>
                                {/* Parte sinistra - Illustrazione */}
                                <Grid item xs={12} md={6} sx={{ 
                                        background: themeColors.gradient.primary,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        p: 4,
                                        borderRadius: { xs: '12px 12px 0 0', md: '12px 0 0 12px' },
                                        position: 'relative',
                                        overflow: 'hidden',
                                        minHeight: { xs: '250px', md: 'auto' }
                                }}>
                                        <Box sx={{ 
                                                textAlign: 'center', 
                                                color: 'white',
                                                zIndex: 1,
                                                position: 'relative'
                                        }}>
                                                {/* Logo/Titolo principale con effetto premium */}
                                                <Box sx={{ mb: 3 }}>
                                                        <Typography 
                                                                variant="h2" 
                                                                component="span"
                                                                sx={{ 
                                                                        fontWeight: 800,
                                                                        fontSize: { xs: '2rem', md: '3.5rem' },
                                                                        letterSpacing: '-0.02em',
                                                                        background: 'linear-gradient(135deg, #FFFFFF 0%, rgba(255,255,255,0.85) 50%, #FFFFFF 100%)',
                                                                        backgroundClip: 'text',
                                                                        WebkitBackgroundClip: 'text',
                                                                        WebkitTextFillColor: 'transparent',
                                                                        textShadow: '0 4px 30px rgba(255,255,255,0.15)',
                                                                        display: 'block',
                                                                        fontFamily: '"Inter", "Roboto", "Helvetica", sans-serif',
                                                                }}
                                                        >
                                                                Click
                                                        </Typography>
                                                        <Typography 
                                                                variant="h5" 
                                                                component="span"
                                                                sx={{ 
                                                                        fontWeight: 300,
                                                                        fontSize: { xs: '1rem', md: '1.25rem' },
                                                                        letterSpacing: '0.3em',
                                                                        textTransform: 'uppercase',
                                                                        opacity: 0.7,
                                                                        display: 'block',
                                                                        my: 0.5,
                                                                }}
                                                        >
                                                                and
                                                        </Typography>
                                                        <Typography 
                                                                variant="h2" 
                                                                component="span"
                                                                sx={{ 
                                                                        fontWeight: 800,
                                                                        fontSize: { xs: '2rem', md: '3.5rem' },
                                                                        letterSpacing: '-0.02em',
                                                                        background: `linear-gradient(135deg, ${themeColors.primary} 0%, ${themeColors.secondary || themeColors.primary} 100%)`,
                                                                        backgroundClip: 'text',
                                                                        WebkitBackgroundClip: 'text',
                                                                        WebkitTextFillColor: 'transparent',
                                                                        filter: 'drop-shadow(0 2px 10px rgba(255,255,255,0.2))',
                                                                        display: 'block',
                                                                        fontFamily: '"Inter", "Roboto", "Helvetica", sans-serif',
                                                                }}
                                                        >
                                                                Care
                                                        </Typography>
                                                </Box>
                                                
                                                {/* Sottotitolo elegante */}
                                                <Typography 
                                                        variant="body1" 
                                                        sx={{ 
                                                                opacity: 0.85,
                                                                fontSize: { xs: '0.9rem', md: '1.1rem' },
                                                                fontWeight: 400,
                                                                letterSpacing: '0.05em',
                                                                maxWidth: '300px',
                                                                mx: 'auto',
                                                                lineHeight: 1.6,
                                                        }}
                                                >
                                                        Piattaforma di gestione integrata per strutture assistenziali
                                                </Typography>
                                                
                                                {/* Linea decorativa */}
                                                <Box sx={{
                                                        width: 60,
                                                        height: 3,
                                                        background: `linear-gradient(90deg, transparent, ${themeColors.primary}, transparent)`,
                                                        mx: 'auto',
                                                        mt: 3,
                                                        borderRadius: 2,
                                                }} />
                                        </Box>
                                        {/* Decorazioni con colori tema */}
                                        <Box sx={{
                                                position: 'absolute',
                                                top: -50,
                                                right: -50,
                                                width: 200,
                                                height: 200,
                                                borderRadius: '50%',
                                                background: themeColors.login.circleDecor1,
                                        }} />
                                        <Box sx={{
                                                position: 'absolute',
                                                bottom: -80,
                                                left: -80,
                                                width: 250,
                                                height: 250,
                                                borderRadius: '50%',
                                                background: themeColors.login.circleDecor2,
                                        }} />
                                        {/* Accento teal */}
                                        <Box sx={{
                                                position: 'absolute',
                                                top: '50%',
                                                right: -100,
                                                width: 200,
                                                height: 200,
                                                borderRadius: '50%',
                                                background: 'rgba(42, 157, 143, 0.1)',
                                                transform: 'translateY(-50%)',
                                        }} />
                                </Grid>

                                {/* Parte destra - Form */}
                                <Grid item xs={12} md={6} sx={{ 
                                        bgcolor: isDarkTheme ? themeColors.login.inputBg : 'white',
                                        p: { xs: 4, md: 6 },
                                        borderRadius: { xs: '0 0 12px 12px', md: '0 12px 12px 0' },
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center'
                                }}>
                                        <Box sx={{ width: '100%' }}>
                                                <Grid container direction={'column'} spacing={3}>

                        <Grid item xs={12} sx={{ textAlign: 'center', mb: 2 }}>
                                <Typography variant="h4" sx={{ fontWeight: 700, color: themeColors.login.heading, mb: 1 }}>
                                        Benvenuto nella
                                </Typography>
                                <Typography variant="h5" sx={{ fontWeight: 600, color: themeColors.login.subheading }}>
                                        Piattaforma Gestionale
                                </Typography>
                        </Grid>

                        {loginType === 1 && (
                                <Grid item xs={12}>
                                        <Button 
                                                variant="text" 
                                                onClick={() => setLoginType(0)}
                                                sx={{ 
                                                        textTransform: 'none',
                                                        color: themeColors.login.subheading,
                                                        mb: 2
                                                }}
                                        >
                                                ← Torna al login cliente
                                        </Button>
                                </Grid>
                        )}                                        <Grid item xs={12}>
                                                <TextField 
                                                        id="email" 
                                                        variant="outlined" 
                                                        fullWidth
                                                        onChange={(e)=> setEmail(e.target.value)}
                                                        value={email}
                                                        placeholder={loginType === 0 ? "Scrivi Indirizzo Email..." : "Username Admin"}
                                                        sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                        borderRadius: '50px',
                                                                        bgcolor: isDarkTheme ? themeColors.border.light : themeColors.login.inputBg,
                                                                        '& fieldset': { borderColor: themeColors.login.inputBorder, borderWidth: 1 },
                                                                        '&:hover fieldset': { borderColor: themeColors.login.inputBorderHover, borderWidth: 1 },
                                                                        '&.Mui-focused fieldset': { borderColor: themeColors.login.inputBorderFocus, borderWidth: 2 },
                                                                        '& input': { 
                                                                                py: 1.5, 
                                                                                px: 2.5,
                                                                                color: themeColors.login.inputText,
                                                                                fontSize: '1rem',
                                                                                '&::placeholder': {
                                                                                        color: themeColors.login.placeholder,
                                                                                        opacity: 1
                                                                                }
                                                                        }
                                                                }
                                                        }}
                                                />
                                        </Grid>
                                        <Grid item xs={12}>
                                                <TextField 
                                                        id="password" 
                                                        type="password" 
                                                        variant="outlined" 
                                                        fullWidth
                                                        onChange={(e)=> setPassword(e.target.value)}
                                                        value={password}
                                                        placeholder="Password"
                                                        sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                        borderRadius: '50px',
                                                                        bgcolor: isDarkTheme ? themeColors.border.light : themeColors.login.inputBg,
                                                                        '& fieldset': { borderColor: themeColors.login.inputBorder, borderWidth: 1 },
                                                                        '&:hover fieldset': { borderColor: themeColors.login.inputBorderHover, borderWidth: 1 },
                                                                        '&.Mui-focused fieldset': { borderColor: themeColors.login.inputBorderFocus, borderWidth: 2 },
                                                                        '& input': { 
                                                                                py: 1.5, 
                                                                                px: 2.5,
                                                                                color: themeColors.login.inputText,
                                                                                fontSize: '1rem',
                                                                                '&::placeholder': {
                                                                                        color: themeColors.login.placeholder,
                                                                                        opacity: 1
                                                                                }
                                                                        }
                                                                }
                                                        }}
                                                />
                                                {loginType === 1 && (
                                                        <Typography variant="caption" sx={{ mt: 1, display: 'block', color: themeColors.login.captionText }}>
                                                                Credenziali predefinite: superadmin / admin123
                                                        </Typography>
                                                )}
                                        </Grid>
                                        <Grid item xs={12}>
                                                <FormControlLabel 
                                                        control={<Checkbox checked={persist} onChange={togglePersist} sx={{ color: themeColors.login.checkboxColor, '&.Mui-checked': { color: themeColors.login.checkboxColor } }} />} 
                                                        label="Ricordami"
                                                        sx={{ 
                                                                color: themeColors.login.rememberText,
                                                                '& .MuiTypography-root': { fontSize: '0.9rem' }
                                                        }}
                                                />
                                        </Grid>
                                        <Grid item xs={12}>
                                                <Button 
                                                        variant="contained" 
                                                        fullWidth 
                                                        onClick={login}
                                                        sx={{
                                                                borderRadius: '50px',
                                                                py: 1.5,
                                                                textTransform: 'uppercase',
                                                                fontWeight: 700,
                                                                fontSize: '1rem',
                                                                bgcolor: themeColors.login.buttonBg,
                                                                boxShadow: `0 4px 14px 0 ${themeColors.login.buttonBg}66`,
                                                                '&:hover': { 
                                                                        bgcolor: themeColors.login.buttonHover,
                                                                        boxShadow: `0 6px 20px 0 ${themeColors.login.buttonBg}80`,
                                                                }
                                                        }}
                                                >
                                                        LOGIN
                                                </Button>
                                        </Grid>
                                        
                                                        {loginType === 0 && (
                                                                <>
                                                                        <Grid item xs={12} sx={{ textAlign: 'center' }}>
                                                                                <Typography variant="body2" sx={{ color: themeColors.login.captionText }}>
                                                                                        Non hai ancora un account?{' '}
                                                                                        <Button 
                                                                                                variant="text" 
                                                                                                onClick={() => navigate('/register')}
                                                                                                sx={{ textTransform: 'none', fontWeight: 600, color: themeColors.login.buttonBg, p: 0, minWidth: 'auto' }}
                                                                                        >
                                                                                                Registrati qui
                                                                                        </Button>
                                                                                </Typography>
                                                                        </Grid>
                                                                        
                                                                        <Grid item xs={12} sx={{ textAlign: 'center' }}>
                                                                                <Button 
                                                                                        variant="text" 
                                                                                        size="small"
                                                                                        onClick={() => setLoginType(1)}
                                                                                        sx={{ 
                                                                                                textTransform: 'none', 
                                                                                                color: themeColors.text.secondary,
                                                                                                fontSize: '0.85rem'
                                                                                        }}
                                                                                >
                                                                                        Accesso Super Admin →
                                                                                </Button>
                                                                        </Grid>
                                                                </>
                                                        )}

                                                </Grid>
                                        </Box>
                                </Grid>
                        </Grid>

                        {/* Dialog per errori di login */}
                        <Dialog
                                open={loginError}
                                onClose={() => setLoginError(false)}
                                aria-labelledby="login-error-dialog"
                                maxWidth="sm"
                                fullWidth
                        >
                                <DialogTitle id="login-error-dialog">
                                        Errore di Accesso
                                </DialogTitle>
                                <DialogContent>
                                        <Alert severity="error" sx={{ mt: 1 }}>
                                                {errorMessage}
                                        </Alert>
                                </DialogContent>
                                <DialogActions>
                                        <Button 
                                                onClick={() => setLoginError(false)} 
                                                variant="contained"
                                                color="primary"
                                        >
                                                OK
                                        </Button>
                                </DialogActions>
                        </Dialog>

                </Box>
);
}
 
export default LoginPage;